import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { HomePage } from "./pages/HomePage";
import { DiscussionPage } from "./pages/DiscussionPage";
import { HistoryPage } from "./pages/HistoryPage";
import { PremiumPage } from "./pages/PremiumPage";
import { CompanyDashboard } from "./pages/CompanyDashboard";
import { FounderDashboard } from "./pages/FounderDashboard";
import { LegalPage } from "./pages/LegalPage";
import { ContactPage } from "./pages/ContactPage";
import { DisclaimerPage } from "./pages/DisclaimerPage";
import { AuthCallback, LoginGate } from "./components/Auth";
import { SupportChat } from "./components/SupportChat";
import { PageviewTracker } from "./lib/pageviewTracker";
import "./App.css";

// ============================================================
// COOKIE CONSENT BANNER (AVG/ePrivacy Directive)
// ============================================================
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) setVisible(true);
  }, []);

  const accept = () => {
    localStorage.setItem("cookie_consent", "accepted");
    localStorage.setItem("cookie_consent_date", new Date().toISOString());
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem("cookie_consent", "declined");
    localStorage.setItem("cookie_consent_date", new Date().toISOString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0e1a] border-t-2 border-[#2d8cf0] p-4 shadow-2xl" data-testid="cookie-consent">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center gap-4">
        <div className="flex-1">
          <p className="text-sm text-white font-medium">We gebruiken alleen technisch noodzakelijke cookies.</p>
          <p className="text-xs text-gray-400 mt-1">
            Geen tracking cookies. Lees ons <Link to="/legal" className="text-[#2d8cf0] hover:underline">privacybeleid</Link>.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={accept}
            className="px-4 py-2 bg-[#2d8cf0] text-white text-xs font-bold hover:bg-[#1a6fd4] transition-colors"
            data-testid="cookie-accept-btn"
          >
            ACCEPTEREN
          </button>
          <button onClick={decline}
            className="px-4 py-2 bg-transparent text-gray-400 text-xs font-bold border border-gray-600 hover:text-white hover:border-gray-400 transition-colors"
            data-testid="cookie-decline-btn"
          >
            WEIGEREN
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// AGE VERIFICATION (18+)
// ============================================================
const AgeVerification = ({ onVerified }) => {
  return (
    <div className="fixed inset-0 z-[60] bg-[#0a0e1a] flex items-center justify-center" data-testid="age-verification">
      <div className="max-w-md w-full mx-4 text-center">
        <img src="/logo-small.png" alt="Debate Engine" className="h-16 w-16 mx-auto mb-6" />
        <h1 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          DEBATE ENGINE
        </h1>
        <p className="text-gray-400 text-sm mb-8">
          Dit platform is bedoeld voor gebruikers van 18 jaar en ouder.
        </p>
        <div className="bg-white/5 border border-white/10 p-6 backdrop-blur-sm">
          <p className="text-white text-sm font-medium mb-6">
            Bevestig dat je 18 jaar of ouder bent om door te gaan.
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={onVerified}
              className="px-6 py-3 bg-[#2d8cf0] text-white font-bold text-sm hover:bg-[#1a6fd4] transition-colors"
              data-testid="age-verify-yes-btn"
            >
              JA, IK BEN 18+
            </button>
            <a href="https://www.google.com"
              className="px-6 py-3 bg-transparent text-gray-400 font-bold text-sm border border-gray-600 hover:text-white hover:border-gray-400 transition-colors"
              data-testid="age-verify-no-btn"
            >
              NEE
            </a>
          </div>
          <p className="text-[10px] text-gray-500 mt-4">
            Door verder te gaan ga je akkoord met onze <Link to="/legal" className="text-[#2d8cf0] hover:underline">voorwaarden</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// NAVIGATION
// ============================================================
const Nav = () => {
  const location = useLocation();
  const links = [
    { to: "/", label: "Debat" },
    { to: "/history", label: "Geschiedenis" },
    { to: "/premium", label: "Premium" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <nav className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a0e1a]/95 text-white border-b border-white/10" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-14">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <div className="h-8 w-8 rounded-md overflow-hidden border border-white/20 group-hover:border-[#2d8cf0]/50 transition-colors">
            <img src="/logo-small.png" alt="Debate Engine" className="h-full w-full object-contain" />
          </div>
          <span className="font-black text-base tracking-tight hidden sm:inline" style={{ fontFamily: 'Chivo, sans-serif' }}>
            DEBATE ENGINE
          </span>
        </Link>
        <div className="flex gap-0.5">
          {links.map(l => (
            <Link
              key={l.to} to={l.to}
              className={`px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider transition-all duration-200 rounded-md ${
                location.pathname === l.to
                  ? "bg-[#2d8cf0] text-white"
                  : "text-gray-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

// ============================================================
// FOOTER
// ============================================================
import { APP_VERSION, APP_NAME, APP_PUBLISHER } from "./lib/version";

const Footer = () => (
  <footer className="bg-[#0a0e1a] text-gray-500 border-t border-white/5 mt-auto">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
      <Link to="/founder" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity" title="Management">
        <img src="/logo-small.png" alt="" className="h-5 w-5 object-contain opacity-50" />
        <span className="text-xs" data-testid="footer-brand">{APP_PUBLISHER} — {APP_NAME} v{APP_VERSION}</span>
      </Link>
      <div className="flex gap-6 flex-wrap justify-center">
        <Link to="/legal" className="text-xs hover:text-white transition-colors" data-testid="footer-legal">Privacy & Voorwaarden</Link>
        <Link to="/disclaimer" className="text-xs hover:text-white transition-colors" data-testid="footer-disclaimer">Disclaimer</Link>
        <Link to="/legal" className="text-xs hover:text-white transition-colors" data-testid="footer-faq">FAQ</Link>
        <Link to="/contact" className="text-xs hover:text-white transition-colors" data-testid="footer-contact">Contact</Link>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-4 -mt-4">
      <p className="text-[10px] text-gray-600 text-center" data-testid="footer-disclaimer-micro">
        Debate Engine kan zich vergissen. Geen rechten te ontlenen aan de output.
      </p>
    </div>
  </footer>
);

// ============================================================
// APP
// ============================================================
// React Router v7 forward-compat flags — extracted to avoid recreating object on every render
const ROUTER_FUTURE_FLAGS = { v7_startTransition: true, v7_relativeSplatPath: true };

function App() {
  const [ageVerified, setAgeVerified] = useState(() => {
    return localStorage.getItem("age_verified") === "true";
  });

  const handleAgeVerified = () => {
    localStorage.setItem("age_verified", "true");
    localStorage.setItem("age_verified_date", new Date().toISOString());
    setAgeVerified(true);
  };

  return (
    <Router future={ROUTER_FUTURE_FLAGS}>
      {!ageVerified && <AgeVerification onVerified={handleAgeVerified} />}
      <div className={`min-h-screen bg-[#FAFAFA] flex flex-col ${!ageVerified ? 'hidden' : ''}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <Toaster position="top-right" />
        <AppRoutes />
        <Footer />
        <CookieConsent />
        <SupportChat />
      </div>
    </Router>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Handle OAuth callback synchronously before rendering routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Nav />
      <PageviewTracker />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/discussion/:id" element={<DiscussionPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/premium" element={<PremiumPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/company" element={<LoginGate requireFounder><CompanyDashboard /></LoginGate>} />
          <Route path="/founder" element={<LoginGate requireFounder><FounderDashboard /></LoginGate>} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
        </Routes>
      </main>
    </>
  );
}

export default App;
